import React from 'react';
import { Alert, Badge, Button, Card, Col, Collapse, Form, Row, Spinner, Table } from 'react-bootstrap';
import { GitAdminIssue, GitIssueRelatedItem } from '../../interfaces';
import GitAdminTimelineChartControl from './GitAdminTimelineChartControl';

type AppsScope = 'central' | 'my' | '';

type TimelineGraph = {
  spentPath: string;
  estimatePath: string;
  minTs: number;
  maxTs: number;
  estimate: number;
  spent: number;
  maxY: number;
};

type Props = {
  showAppsTimeline: boolean;
  setShowAppsTimeline: React.Dispatch<React.SetStateAction<boolean>>;
  appsScope: AppsScope;
  setAppsScope: React.Dispatch<React.SetStateAction<AppsScope>>;
  selectedAppsIssueId: number | null;
  setSelectedAppsIssueId: React.Dispatch<React.SetStateAction<number | null>>;
  appsIssues: GitAdminIssue[];
  selectedAppsIssue: GitAdminIssue | null;
  appsIssueRefreshLoading: boolean;
  refreshSelectedAppsIssue: () => Promise<void>;
  appsTimeEventsLoading: boolean;
  refreshAppsTimeline: () => void;
  appsTimeEventsError: string | null;
  appsIssueRefreshMessage: string | null;
  appsTimelineGraph: TimelineGraph | null;
  formatDuration: (seconds: number) => string;
  relatedAppsIssues: GitIssueRelatedItem[];
  repositoriesById: Map<number, { namespace?: string; name?: string }>;
  formatHoursAxisLabel: (seconds: number) => string;
  formatTimelineTick: (timestamp: number, minTs: number, maxTs: number) => string;
  appsTimelineDebug: { request: unknown; response: unknown } | null;
  renderTimeProgress: (timeSpentSeconds?: number | null, timeEstimateSeconds?: number | null) => React.ReactNode;
};

const GitAdminAppsTimelineCard: React.FC<Props> = ({
  showAppsTimeline,
  setShowAppsTimeline,
  appsScope,
  setAppsScope,
  selectedAppsIssueId,
  setSelectedAppsIssueId,
  appsIssues,
  selectedAppsIssue,
  appsIssueRefreshLoading,
  refreshSelectedAppsIssue,
  appsTimeEventsLoading,
  refreshAppsTimeline,
  appsTimeEventsError,
  appsIssueRefreshMessage,
  appsTimelineGraph,
  formatDuration,
  relatedAppsIssues,
  repositoriesById,
  formatHoursAxisLabel,
  formatTimelineTick,
  appsTimelineDebug,
  renderTimeProgress
}) => {
  return (
    <Card className="shadow-sm mb-4">
      <Card.Header className="d-flex flex-wrap justify-content-between align-items-center gap-2">
        <span>Timeline apps</span>
        <Button
          size="sm"
          variant="outline-secondary"
          onClick={() => setShowAppsTimeline(value => !value)}
        >
          {showAppsTimeline ? 'Masquer' : 'Afficher'}
        </Button>
      </Card.Header>
      <Collapse in={showAppsTimeline}>
        <div>
          <Card.Body>
            <Row className="g-3 mb-3">
              <Col md={4}>
                <Form.Group>
                  <Form.Label>Scope</Form.Label>
                  <Form.Select
                    value={appsScope}
                    onChange={(event) => setAppsScope(event.target.value as AppsScope)}
                  >
                    <option value="">Aucun scope</option>
                    <option value="central">central</option>
                    <option value="my">my</option>
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={8}>
                <Form.Group>
                  <Form.Label>Issue apps</Form.Label>
                  <Form.Select
                    value={selectedAppsIssueId ?? ''}
                    onChange={(event) => setSelectedAppsIssueId(event.target.value ? Number(event.target.value) : null)}
                  >
                    <option value="">Aucune issue sélectionnée</option>
                    {appsIssues.length === 0 ? (
                      <option value="" disabled>Aucune issue apps pour ce scope</option>
                    ) : (
                      appsIssues.map(issue => (
                        <option key={issue.id} value={issue.id}>
                          #{issue.gitlabIssueIid} - {issue.title}
                        </option>
                      ))
                    )}
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={12} className="d-flex justify-content-end">
                <Button
                  size="sm"
                  variant="outline-success"
                  className="me-2"
                  disabled={!selectedAppsIssue || appsIssueRefreshLoading}
                  onClick={refreshSelectedAppsIssue}
                >
                  {appsIssueRefreshLoading ? 'Rafraichissement issue...' : 'Rafraichir issue apps'}
                </Button>
                <Button
                  size="sm"
                  variant="outline-primary"
                  disabled={!selectedAppsIssue || appsTimeEventsLoading}
                  onClick={refreshAppsTimeline}
                >
                  Rafraichir graph
                </Button>
              </Col>
            </Row>

            {appsTimeEventsError && (
              <Alert variant="warning" className="mb-3">
                {appsTimeEventsError}
              </Alert>
            )}

            {appsIssueRefreshMessage && (
              <Alert variant="info" className="mb-3">
                {appsIssueRefreshMessage}
              </Alert>
            )}

            {appsTimeEventsLoading && (
              <div className="d-flex align-items-center gap-2 text-muted">
                <Spinner animation="border" size="sm" />
                Chargement de la timeline...
              </div>
            )}

            {!appsTimeEventsLoading && !appsTimeEventsError && !selectedAppsIssue && (
              <div className="text-muted">Sélectionnez un scope et une issue pour afficher la timeline.</div>
            )}

            {!appsTimeEventsLoading && !appsTimeEventsError && selectedAppsIssue && !appsTimelineGraph && (
              <div className="text-muted">Manque de données.</div>
            )}

            {!appsTimeEventsLoading && selectedAppsIssue && appsTimelineGraph && (
              <>
                <div className="d-flex flex-wrap gap-3 mb-3">
                  <Badge bg="secondary">Issue #{selectedAppsIssue.gitlabIssueIid}</Badge>
                  <Badge bg="info">Estimé: {formatDuration(appsTimelineGraph.estimate)}</Badge>
                  <Badge bg="success">Consommé: {formatDuration(Math.max(0, appsTimelineGraph.spent ?? 0))}</Badge>
                  <Badge bg="dark">Liées: {relatedAppsIssues.length}</Badge>
                  <Button
                    size="sm"
                    variant="outline-success"
                    disabled={appsIssueRefreshLoading}
                    onClick={refreshSelectedAppsIssue}
                  >
                    {appsIssueRefreshLoading ? 'Rafraichissement...' : 'Rafraichir données issue sélectionnée'}
                  </Button>
                </div>

                <div className="mb-3">
                  <div className="fw-semibold">{selectedAppsIssue.title}</div>
                  <div className="small text-muted">
                    {repositoriesById.get(selectedAppsIssue.gitRepositoryId)?.namespace ?? 'N/A'} / {repositoriesById.get(selectedAppsIssue.gitRepositoryId)?.name ?? 'N/A'}
                    {' '}| État: {selectedAppsIssue.state}
                    {' '}| Assignee: {selectedAppsIssue.assigneeName ?? 'N/A'}
                  </div>
                </div>

                <GitAdminTimelineChartControl
                  graph={appsTimelineGraph}
                  formatHoursAxisLabel={formatHoursAxisLabel}
                  formatTimelineTick={formatTimelineTick}
                />

                {appsTimelineDebug && (
                  <details className="mt-3">
                    <summary className="small text-muted">Debug API timeline</summary>
                    <pre className="small bg-white border rounded p-2 mt-2 mb-0 gitadmin-debug-pre">
{JSON.stringify(appsTimelineDebug, null, 2)}
                    </pre>
                  </details>
                )}

                <div className="mt-4">
                  <h6 className="mb-3">Issues et tâches associées</h6>
                  {relatedAppsIssues.length === 0 ? (
                    <div className="text-muted">Aucune issue ou tâche liée pour cette issue.</div>
                  ) : (
                    <div className="table-responsive">
                      <Table striped hover size="sm" className="align-middle mb-0">
                        <thead>
                          <tr>
                            <th>#</th>
                            <th>Titre</th>
                            <th>État</th>
                            <th>Assignee</th>
                            <th>Progress</th>
                            <th>Labels</th>
                            <th>Lien</th>
                          </tr>
                        </thead>
                        <tbody>
                          {relatedAppsIssues.map((relatedIssue: GitIssueRelatedItem) => (
                            <tr key={relatedIssue.id}>
                              <td>#{relatedIssue.iid}</td>
                              <td>{relatedIssue.title}</td>
                              <td>{relatedIssue.state}</td>
                              <td>{relatedIssue.assigneeName ?? 'N/A'}</td>
                              <td>{renderTimeProgress(relatedIssue.timeSpentSeconds, relatedIssue.timeEstimateSeconds)}</td>
                              <td>
                                {relatedIssue.labels.length === 0 ? (
                                  <span className="text-muted">-</span>
                                ) : (
                                  <div className="d-flex flex-wrap gap-1">
                                    {relatedIssue.labels.map(label => (
                                      <Badge key={`${relatedIssue.id}-${label}`} bg="secondary" pill>
                                        {label}
                                      </Badge>
                                    ))}
                                  </div>
                                )}
                              </td>
                              <td>
                                {relatedIssue.webUrl ? (
                                  <a href={relatedIssue.webUrl} target="_blank" rel="noopener noreferrer">
                                    Ouvrir
                                  </a>
                                ) : (
                                  <span className="text-muted">-</span>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    </div>
                  )}
                </div>
              </>
            )}
          </Card.Body>
        </div>
      </Collapse>
    </Card>
  );
};

export default GitAdminAppsTimelineCard;
